/**
 * ThornhavenPerimeter — natural mythological-Midgard palisade.
 *
 * Replaces the old Kenney Castle-Kit stone wall with a hand-built rough-timber
 * palisade: close-set pointed logs on a low earth mound, bound by a horizontal
 * rail, with occasional rough field-stone reinforcement, taller carved corner
 * god-posts and flanking gate posts with a runed lintel over each road gate.
 *
 * It should read as part of the landscape, not a bright game boundary. Pure
 * three.js JSX primitives (no GLTF). The x=0 north-south road passes through
 * two open gates (no collider there). Collision = one continuous box per run.
 */

import { RigidBody, CuboidCollider } from '@react-three/rapier';
import { useMemo } from 'react';

const COL = {
  logA: '#5a4026', logB: '#6e5433', logDark: '#3a2a1a',
  dirt: '#4f3a26', stone: '#77756e', stoneDark: '#53514b', rune: '#5fd6c4', beam: '#2a1c10',
};

// Perimeter rectangle (encloses the Yggdrasil hub, the road and the Mead Hall at x=28).
const X0 = -34, X1 = 46, Z0 = -32, Z1 = 34;
const GATE = 4;            // half-width of each road gate opening (on the x=0 road)
const SPACING = 1.05;      // log spacing
const RAIL_Y = 2.45;
const COL_H = 3.4;

const RUNS = [
  { axis: 'x', fixed: Z0, from: X0, to: -GATE },
  { axis: 'x', fixed: Z0, from: GATE, to: X1 },
  { axis: 'x', fixed: Z1, from: X0, to: -GATE },
  { axis: 'x', fixed: Z1, from: GATE, to: X1 },
  { axis: 'z', fixed: X0, from: Z0, to: Z1 },
  { axis: 'z', fixed: X1, from: Z0, to: Z1 },
];

const CORNER_POSTS = [[X0, Z0], [X1, Z0], [X0, Z1], [X1, Z1]];
const GATE_POSTS = [[-GATE, Z0], [GATE, Z0], [-GATE, Z1], [GATE, Z1]];
const GATES = [{ x: 0, z: Z0 }, { x: 0, z: Z1 }];

// Deterministic pseudo-random so logs don't jitter between renders.
const rand = (s) => { const x = Math.sin(s * 127.1 + 311.7) * 43758.5; return x - Math.floor(x); };

function buildPalisade() {
  const logs = [];
  const rails = [];
  const mounds = [];
  const stones = [];
  const colliders = [];
  let seed = 1;

  for (const run of RUNS) {
    const length = Math.abs(run.to - run.from);
    const n = Math.max(1, Math.round(length / SPACING));
    const step = length / n;
    const dir = Math.sign(run.to - run.from) || 1;
    for (let i = 0; i < n; i++) {
      const c = run.from + dir * step * (i + 0.5);
      const h = 2.9 + rand(seed++) * 0.7;
      const col = i % 2 ? COL.logA : COL.logB;
      const x = run.axis === 'x' ? c : run.fixed;
      const z = run.axis === 'x' ? run.fixed : c;
      logs.push({ pos: [x, h / 2, z], h, col });
      // occasional rough field-stone reinforcement
      if (i % 9 === 4) stones.push({ pos: [x + (run.axis === 'x' ? 0 : 0.8), 0.5, z + (run.axis === 'x' ? 0.8 : 0)], s: 0.7 + rand(seed++) * 0.5 });
    }
    const mid = (run.from + run.to) / 2;
    const mx = run.axis === 'x' ? mid : run.fixed;
    const mz = run.axis === 'x' ? run.fixed : mid;
    if (run.axis === 'x') {
      rails.push({ pos: [mx, RAIL_Y, mz], args: [length, 0.16, 0.16] });
      mounds.push({ pos: [mx, 0.25, mz], args: [length, 0.5, 1.2] });
      colliders.push({ args: [length / 2, COL_H / 2, 0.5], pos: [mx, COL_H / 2, mz] });
    } else {
      rails.push({ pos: [mx, RAIL_Y, mz], args: [0.16, 0.16, length] });
      mounds.push({ pos: [mx, 0.25, mz], args: [1.2, 0.5, length] });
      colliders.push({ args: [0.5, COL_H / 2, length / 2], pos: [mx, COL_H / 2, mz] });
    }
  }
  return { logs, rails, mounds, stones, colliders };
}

const P = buildPalisade();

function Log({ pos, h, col }) {
  return (
    <group position={pos}>
      <mesh castShadow>
        <cylinderGeometry args={[0.2, 0.24, h, 6]} />
        <meshStandardMaterial color={col} roughness={0.96} />
      </mesh>
      <mesh position={[0, h / 2 + 0.2, 0]} castShadow>
        <coneGeometry args={[0.22, 0.42, 6]} />
        <meshStandardMaterial color={COL.logDark} roughness={0.95} />
      </mesh>
    </group>
  );
}

function CarvedPost({ pos, h = 4.6 }) {
  const runes = [];
  for (let i = 1; i < Math.floor(h / 0.7); i++) runes.push(i * 0.7);
  return (
    <group position={[pos[0], 0, pos[1]]}>
      <mesh position={[0, h / 2, 0]} castShadow>
        <boxGeometry args={[0.46, h, 0.46]} />
        <meshStandardMaterial color={COL.logDark} roughness={0.95} />
      </mesh>
      {runes.map((y, i) => (
        <mesh key={i} position={[0, y, 0.24]}>
          <boxGeometry args={[0.16, 0.22, 0.05]} />
          <meshStandardMaterial color={COL.rune} emissive={COL.rune} emissiveIntensity={0.7} />
        </mesh>
      ))}
      <mesh position={[0, h + 0.2, 0]} castShadow>
        <coneGeometry args={[0.3, 0.6, 6]} />
        <meshStandardMaterial color={COL.logA} roughness={0.95} />
      </mesh>
    </group>
  );
}

export default function ThornhavenPerimeter() {
  const content = useMemo(() => P, []);
  return (
    <group name="thornhaven-perimeter">
      {/* earth mounds */}
      {content.mounds.map((m, i) => (
        <mesh key={`mound-${i}`} position={m.pos} receiveShadow>
          <boxGeometry args={m.args} />
          <meshStandardMaterial color={COL.dirt} roughness={1} />
        </mesh>
      ))}
      {/* pointed logs */}
      {content.logs.map((l, i) => <Log key={`log-${i}`} {...l} />)}
      {/* binding rails */}
      {content.rails.map((r, i) => (
        <mesh key={`rail-${i}`} position={r.pos}>
          <boxGeometry args={r.args} />
          <meshStandardMaterial color={COL.beam} roughness={0.9} />
        </mesh>
      ))}
      {/* rough field-stone reinforcement */}
      {content.stones.map((s, i) => (
        <mesh key={`stone-${i}`} position={s.pos} rotation={[0, i * 0.7, 0]} castShadow>
          <dodecahedronGeometry args={[s.s, 0]} />
          <meshStandardMaterial color={i % 2 ? COL.stone : COL.stoneDark} roughness={1} />
        </mesh>
      ))}
      {/* carved corner + gate god-posts */}
      {CORNER_POSTS.map((p, i) => <CarvedPost key={`cp-${i}`} pos={p} h={5} />)}
      {GATE_POSTS.map((p, i) => <CarvedPost key={`gp-${i}`} pos={p} h={4.4} />)}
      {/* runed gate lintels over the open road gates */}
      {GATES.map((g, i) => (
        <group key={`gate-${i}`}>
          <mesh position={[g.x, 4.2, g.z]} castShadow>
            <boxGeometry args={[GATE * 2 + 0.9, 0.5, 0.6]} />
            <meshStandardMaterial color={COL.beam} roughness={0.9} />
          </mesh>
          {[-2, 0, 2].map((dx, j) => (
            <mesh key={j} position={[g.x + dx, 4.2, g.z + 0.32]}>
              <boxGeometry args={[0.18, 0.3, 0.05]} />
              <meshStandardMaterial color={COL.rune} emissive={COL.rune} emissiveIntensity={0.7} />
            </mesh>
          ))}
        </group>
      ))}

      {/* Collision: one continuous box per run. Gate openings have none, so the
          x=0 road stays physically passable. */}
      <RigidBody type="fixed" colliders={false}>
        {content.colliders.map((c, i) => (
          <CuboidCollider key={`wc-${i}`} args={c.args} position={c.pos} />
        ))}
      </RigidBody>
    </group>
  );
}
